export const PRODUCT_LIFECYCLE_DEFINITIONS = [
  {
    value: 'active-development',
    label: 'Active Development',
    description: 'Active feature development and normal operational support.',
    current: true,
  },
  {
    value: 'maintenance-mode',
    label: 'Maintenance Mode',
    description:
      'Supported with fixes, security, compliance, and essential maintenance.',
    current: true,
  },
  {
    value: 'end-of-life',
    label: 'End of Life',
    description:
      'Product has reached end of life and decommissioning is planned.',
    current: true,
  },
  {
    value: 'decommissioned',
    label: 'Decommissioned',
    description:
      'No longer operational or offered; retained for historical reference.',
    current: false,
  },
] as const;

export type ProductLifecycleStatus =
  (typeof PRODUCT_LIFECYCLE_DEFINITIONS)[number]['value'];

export const PRODUCT_LIFECYCLE_STATUSES: readonly ProductLifecycleStatus[] =
  PRODUCT_LIFECYCLE_DEFINITIONS.map(definition => definition.value);

export const DEFAULT_PRODUCT_LIFECYCLE: ProductLifecycleStatus =
  'active-development';
export const DEFAULT_PRODUCT_LIFECYCLE_FILTER = 'current';
export const ALL_PRODUCT_LIFECYCLE_FILTER_LABEL =
  'All (includes Decommissioned)';
export const CURRENT_PRODUCT_LIFECYCLE_FILTER_LABEL =
  'All (excludes Decommissioned)';

export const CURRENT_PRODUCT_LIFECYCLES: ReadonlySet<string> = new Set(
  PRODUCT_LIFECYCLE_DEFINITIONS.filter(definition => definition.current).map(
    definition => definition.value,
  ),
);

const LEGACY_PRODUCT_LIFECYCLE_LABELS: Readonly<Record<string, string>> = {
  active: 'Active (Legacy)',
  inactive: 'Inactive (Legacy)',
  deprecated: 'Deprecated (Legacy)',
};

export const LEGACY_EOL_ANNOUNCED_PRODUCT_LIFECYCLE = 'eol-announced';

/** Normalize lifecycle values read from catalog during the status migration. */
export function normalizeProductLifecycleValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  return normalized === LEGACY_EOL_ANNOUNCED_PRODUCT_LIFECYCLE
    ? 'end-of-life'
    : normalized;
}

export function isProductLifecycleStatus(
  value: unknown,
): value is ProductLifecycleStatus {
  return (
    typeof value === 'string' &&
    PRODUCT_LIFECYCLE_STATUSES.includes(value as ProductLifecycleStatus)
  );
}

export function isCurrentProductLifecycle(value: string): boolean {
  // Keep unmigrated products visible during compatibility rollouts.
  const normalized = normalizeProductLifecycleValue(value);
  return normalized === 'active' || CURRENT_PRODUCT_LIFECYCLES.has(normalized);
}

export function formatProductLifecycleStatus(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return 'Unknown';
  }
  const normalized = normalizeProductLifecycleValue(value);
  const definition = PRODUCT_LIFECYCLE_DEFINITIONS.find(
    item => item.value === normalized,
  );
  if (definition) {
    return definition.label;
  }
  if (LEGACY_PRODUCT_LIFECYCLE_LABELS[normalized]) {
    return LEGACY_PRODUCT_LIFECYCLE_LABELS[normalized];
  }
  return value;
}

export function formatProductLifecycleFilterOption(value: string): string {
  if (value === 'all') {
    return ALL_PRODUCT_LIFECYCLE_FILTER_LABEL;
  }
  if (value === DEFAULT_PRODUCT_LIFECYCLE_FILTER) {
    return CURRENT_PRODUCT_LIFECYCLE_FILTER_LABEL;
  }
  return formatProductLifecycleStatus(value);
}

export function productLifecycleSortIndex(value: string): number {
  const index = PRODUCT_LIFECYCLE_STATUSES.indexOf(
    normalizeProductLifecycleValue(value) as ProductLifecycleStatus,
  );
  return index === -1 ? PRODUCT_LIFECYCLE_STATUSES.length : index;
}
